import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Copy, Link2, ShieldCheck, RefreshCw, FileArchive, CheckCircle2, XCircle, ExternalLink, QrCode, MessageCircle } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * Admin / doctor card that generates a case-scoped DICOM upload link for
 * a patient and shows the history of uploads. Mount inside any case detail
 * screen (Admin Dashboard, Case Detail, Hospital Dashboard).
 */
export default function DicomUploadManager({ caseId }) {
  const [state, setState] = useState({ links: [], uploads: [], case_ref: "" });
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    if (!caseId) return;
    setLoading(true);
    try {
      const r = await api.get(`/admin/cases/${caseId}/dicom-links`);
      setState(r.data);
    } catch (e) {
      // Silent — 403 is fine, this card is optional in some contexts.
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [caseId]);

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api.post(`/admin/cases/${caseId}/dicom-link`);
      toast.success(`Upload link generated — valid ${r.data.expires_in_hours}h`);
      // Copy immediately for convenience
      try { await navigator.clipboard.writeText(r.data.url); toast.info("Link copied to clipboard"); } catch { /* noop */ }
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not generate link.";
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  const copy = async (url) => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    catch { toast.error("Copy failed — please long-press to copy."); }
  };

  // Open WhatsApp with a prefilled message to the patient. Falls back to
  // the generic wa.me composer if we don't have a phone on file.
  const sendOnWhatsApp = (url) => {
    const phone = (state.patient?.phone || "").replace(/\D/g, "");
    const ref = state.case_ref || "";
    const msg =
      `Hi${state.patient?.name ? " " + state.patient.name.split(" ")[0] : ""}, ` +
      `here is your secure upload link for NeuroOpinion case ${ref}. ` +
      `Please upload your CT/MRI DICOM study (or ZIP) using this link — valid for 72 hours:\n${url}`;
    const target = phone
      ? `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`
      : `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(target, "_blank", "noopener");
  };

  const revoke = async (token) => {
    if (!window.confirm("Revoke this upload link? Patients using it will need a fresh one.")) return;
    try {
      await api.post(`/admin/dicom-links/${token}/revoke`);
      toast.success("Link revoked");
      load();
    } catch { toast.error("Revoke failed"); }
  };

  const openDownload = async (uploadId) => {
    try {
      const r = await api.get(`/admin/dicom-uploads/${uploadId}/download-url`);
      window.open(r.data.url, "_blank");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open file.");
    }
  };

  const fmtBytes = (n) => {
    if (!n) return "—";
    const u = ["B", "KB", "MB", "GB", "TB"]; let i = 0; let x = n;
    while (x >= 1024 && i < u.length - 1) { x /= 1024; i++; }
    return `${x.toFixed(x < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
  };

  const activeLinks = state.links.filter((l) => !l.revoked && new Date(l.expires_at) > new Date());
  const publicOrigin = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/api\/?$/, "");

  return (
    <Card className="p-5 border-slate-200" data-testid="dicom-upload-manager">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <FileArchive className="w-5 h-5 text-indigo-600"/>
          <div>
            <div className="font-heading font-semibold text-slate-900">Large DICOM upload</div>
            <div className="text-xs text-slate-500">Send a secure link to the patient to upload multi-GB CT / MRI studies</div>
          </div>
        </div>
        <Button
          onClick={generate} disabled={generating}
          className="bg-indigo-600 hover:bg-indigo-700 h-9"
          data-testid="dicom-generate-link"
        >
          {generating ? <RefreshCw className="w-4 h-4 mr-1.5 animate-spin"/> : <Link2 className="w-4 h-4 mr-1.5"/>}
          {generating ? "Generating…" : "Generate DICOM Upload Link"}
        </Button>
      </div>

      {loading && <div className="mt-4 text-xs text-slate-500">Loading…</div>}

      {/* Active links */}
      {activeLinks.length > 0 && (
        <div className="mt-4 space-y-3" data-testid="dicom-active-links">
          {activeLinks.map((l) => {
            const fullUrl = `${publicOrigin}/upload/${l.token}`;
            return (
              <div key={l.token} className="rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-sm">
                <div className="flex items-start gap-3 flex-wrap sm:flex-nowrap">
                  <ShieldCheck className="w-4 h-4 text-indigo-700 mt-0.5 shrink-0"/>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-xs text-slate-700 truncate" data-testid={`dicom-link-${l.token}`}>
                      {fullUrl}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5">
                      Expires {new Date(l.expires_at).toLocaleString()} · {l.upload_count || 0} upload(s) so far
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => copy(fullUrl)} data-testid={`dicom-copy-${l.token}`}>
                        <Copy className="w-3 h-3 mr-1"/> Copy
                      </Button>
                      <Button
                        size="sm" variant="outline"
                        className="h-7 text-xs border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                        onClick={() => sendOnWhatsApp(fullUrl)}
                        data-testid={`dicom-wa-${l.token}`}
                        title={state.patient?.phone ? `Send to ${state.patient.phone}` : "Compose in WhatsApp"}
                      >
                        <MessageCircle className="w-3 h-3 mr-1"/>
                        {state.patient?.phone ? "Send on WhatsApp" : "Share on WhatsApp"}
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-500 hover:text-rose-600" onClick={() => revoke(l.token)} data-testid={`dicom-revoke-${l.token}`}>
                        Revoke
                      </Button>
                    </div>
                  </div>
                  {/* QR — patient scans it from their phone directly */}
                  <div className="shrink-0 flex flex-col items-center gap-1 self-start" data-testid={`dicom-qr-${l.token}`}>
                    <div className="bg-white p-1.5 rounded border border-indigo-200 shadow-sm">
                      <QRCodeSVG value={fullUrl} size={88} level="M" includeMargin={false}/>
                    </div>
                    <div className="text-[9px] text-slate-500 flex items-center gap-0.5">
                      <QrCode className="w-2.5 h-2.5"/> Scan on phone
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Uploads table */}
      {state.uploads?.length > 0 && (
        <div className="mt-5">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Uploads</div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="dicom-uploads-table">
              <thead>
                <tr className="text-[10px] uppercase text-slate-500 border-b border-slate-200">
                  <th className="text-left py-2 pr-3">File</th>
                  <th className="text-left py-2 pr-3">Size</th>
                  <th className="text-left py-2 pr-3">Status</th>
                  <th className="text-left py-2 pr-3">When</th>
                  <th className="text-right py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {state.uploads.map((u) => (
                  <tr key={u.id} className="border-b border-slate-100" data-testid={`dicom-upload-row-${u.id}`}>
                    <td className="py-2 pr-3 truncate max-w-[200px]" title={u.filename}>{u.filename}</td>
                    <td className="py-2 pr-3 tabular-nums">{fmtBytes(u.actual_size ?? u.declared_size)}</td>
                    <td className="py-2 pr-3">
                      {u.status === "completed" && <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-3 h-3"/> Completed</span>}
                      {u.status === "uploading" && <span className="text-blue-700">Uploading…</span>}
                      {u.status === "initialised" && <span className="text-slate-500">Not started</span>}
                      {u.status === "aborted" && <span className="text-slate-500">Aborted</span>}
                      {u.status === "failed" && <span className="inline-flex items-center gap-1 text-rose-700"><XCircle className="w-3 h-3"/> Failed</span>}
                    </td>
                    <td className="py-2 pr-3 text-slate-500">
                      {u.completed_at ? new Date(u.completed_at).toLocaleString()
                        : u.started_at ? new Date(u.started_at).toLocaleString() : "—"}
                    </td>
                    <td className="py-2 text-right">
                      {u.status === "completed" && (
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openDownload(u.id)} data-testid={`dicom-download-${u.id}`}>
                          <ExternalLink className="w-3 h-3 mr-1"/> Open
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeLinks.length === 0 && state.uploads?.length === 0 && !loading && (
        <p className="mt-4 text-xs text-slate-500">
          No upload links yet. Tap <b>Generate DICOM Upload Link</b> to create one — the link is valid for 72 hours and you can copy it to send on WhatsApp or email.
        </p>
      )}
    </Card>
  );
}
